// @flow
import moment from "moment"
import { Map, List, Record, Set } from "immutable"

import { getOr } from "common/utils/get"
import Flight from "./Flight"
import Transfer from "./Transfer"
import type { PART_TYPE } from "../../booking/scenes/ReservationForm/consts/reservationConsts"

const TripRecord = Record({
  flights: List(),
  parts: List(),
})

// eslint-disable-next-line flowtype/no-weak-types
export type Stop = Map<string, any> // eslint-disable-line flowtype/no-weak-types

export default class Trip extends TripRecord {
  getId(): string {
    return this.get("flights")
      .reduce((res, flight) => res.concat([flight.id]), [])
      .join("|")
  }

  // There should NEVER be trip with more flights, if the only flight is hidden
  isHidden(): boolean {
    return this.get("flights").every(flight => flight.isHidden)
  }

  getStopsCount(): number {
    return this.get("flights").count() - 1
  }

  getStops(): List<Stop> {
    return List(this.get("flights").map(flight => this.getPrevStop(flight))).shift() // remove the first 'null'
  }

  getFlightsNumbers(delimiter: string = ""): Array<string> {
    return this.get("parts")
      .map(x => x.getFlightNumber(delimiter))
      .toArray()
      .filter(Boolean)
  }

  /**
   * @param part - Part object of this trip - transfer / flight
   * @returns {null | stop}
   */
  getPrevStop(part: Flight): ?Stop {
    const parts = this.get("parts")
    const index = parts.indexOf(part)
    const previousArrival =
      parts.getIn([index - 1, "status", "changedArrivalWhere", "code"]) ||
      parts.getIn([index - 1, "arrival", "where", "code"])
    const nextDeparture =
      part.getIn(["status", "changedDepartureWhere", "code"]) ||
      part.getIn(["departure", "where", "code"])

    const differentAirport = previousArrival !== nextDeparture

    if (index !== 0) {
      const previousPart = parts.get(index - 1)

      const previousStopArrival = getOr(
        previousPart.getIn(["arrival", "when", "utc"]),
        ["status", "changedTime", "arrival", "when", "utc"],
        previousPart,
      )

      const currentStopDeparture = getOr(
        part.getIn(["departure", "when", "utc"]),
        ["status", "changedTime", "departure", "when", "utc"],
        part,
      )

      const duration = moment.duration(currentStopDeparture.diff(previousStopArrival))

      return Map({
        id: part.getIn(["departure", "where", "id"]),
        name: part.getIn(["departure", "where", "name"]),
        country: part.getIn(["departure", "where", "country"]),
        code: part.getIn(["departure", "where", "code"]),
        duration,
        bagsRecheckRequired: part.get("bagsRecheckRequired"),
        hasGuarantee: part.get("guarantee"),
        differentAirport: differentAirport
          ? Map({
              arrival: previousArrival,
              departure: nextDeparture,
            })
          : null,
        containsGroundTransport: this.containsGroundTransport(),
        isLayoverBetweenFlights: this.isLayoverBetweenFlights(part),
      })
    }
    return null
  }

  // eslint-disable-next-line flowtype/no-weak-types
  getDeparture(): Map<string, any> {
    // eslint-disable-line flowtype/no-weak-types
    return this.get("flights")
      .first()
      .get("departure")
  }

  // eslint-disable-next-line flowtype/no-weak-types
  getArrival(): Map<string, any> {
    // eslint-disable-line flowtype/no-weak-types
    return this.get("flights")
      .last()
      .get("arrival")
  }

  // eslint-disable-next-line flowtype/no-weak-types
  getDepartureWithoutHiddenFlights(): Map<string, any> {
    const first = this.get("flights").find(x => !x.get("isHidden"))
    return first ? first.get("departure") : null
  }

  // eslint-disable-next-line flowtype/no-weak-types
  getArrivalWithoutHiddenFlights(): Map<string, any> {
    const last = this.get("flights").findLast(x => !x.get("isHidden"))
    return last ? last.get("arrival") : null
  }

  getDuration(): moment$MomentDuration {
    return moment.duration(
      this.getArrival()
        .get("when")
        .get("utc")
        .diff(
          this.getDeparture()
            .get("when")
            .get("utc"),
        ),
    )
  }

  getDurationWithoutHiddenFlights(): ?moment$MomentDuration {
    const arrivalWithoutHiddenFlights = this.getArrivalWithoutHiddenFlights()
    const departureWithoutHiddenFlights = this.getDepartureWithoutHiddenFlights()
    if (!arrivalWithoutHiddenFlights || !departureWithoutHiddenFlights) {
      return null
    }
    return moment.duration(
      arrivalWithoutHiddenFlights
        .get("when")
        .get("utc")
        .diff(departureWithoutHiddenFlights.get("when").get("utc")),
    )
  }

  // eslint-disable-next-line flowtype/no-weak-types
  getAllAirlines(): List<Map<string, any>> {
    // eslint-disable-line flowtype/no-weak-types
    return this.get("flights")
      .reduce((airlines, flight) => {
        const code = flight.get("airline").get("code")
        const type = flight.get("type")

        return airlines.set(code, flight.get("airline").set("type", type))
      }, Map())
      .toList()
  }

  getFlightsCount(): number {
    return this.get("flights").count()
  }

  getTypes(): Set<PART_TYPE> {
    return Set(this.get("flights").map(flight => flight.get("type")))
  }

  /**
   * Check whether departure is different day than arrival
   */
  isLocalOverNight(): boolean {
    return (
      this.getArrival()
        .get("when")
        .get("local")
        .get("M") !==
        this.getDeparture()
          .get("when")
          .get("local")
          .get("M") ||
      this.getArrival()
        .get("when")
        .get("local")
        .get("D") !==
        this.getDeparture()
          .get("when")
          .get("local")
          .get("D")
    )
  }

  isChangeAllowed(hoursAllowedToChange: number): boolean {
    const departure = this.getDeparture()
    const time = departure && departure.getIn(["when", "utc"])

    return time
      ? time
          .clone()
          .subtract(hoursAllowedToChange, "hours")
          .isAfter()
      : false
  }

  getPrioCabinBagsFlights(): Flight[] {
    return this.get("flights")
      .filter(flight => flight.get("isCabinBagsWithPriorityBoarding"))
      .toArray()
  }

  getPriorityBoardingFlights(): Flight[] {
    return this.get("flights")
      .filter(flight => flight.get("priorityBoarding"))
      .toArray()
  }

  isLayoverBetweenFlights(part: Flight): boolean {
    const parts = this.get("parts")
    const index = parts.indexOf(part)
    return part.get("type") === "flight" && parts.get(index - 1).get("type") === "flight"
  }

  containsGroundTransport(): boolean {
    const parts = this.get("parts")
    const groundTransports = ["bus", "train"]
    return parts.some(part => groundTransports.includes(part.get("type")))
  }

  static fromPlainJS({ flights, parts }: any) {
    return new Trip({
      flights: List(flights.map(Flight.fromPlainJS)),
      parts: List(
        parts.map(part =>
          part.type === "flight" ? Flight.fromPlainJS(part) : Transfer.fromPlainJS(part),
        ),
      ),
    })
  }
}
